import React, { FC, useMemo } from 'react';
import DomainsInput from '@components/EntityDrawers/components/inputs/DomainDropdown/components/DomainsInput';
import VirtualDomainsAccordion from '@components/EntityDrawers/components/inputs/DomainDropdown/components/VirtualDomainsAccordion';
import useVirtualDomains from '@components/EntityDrawers/hooks/useVirtualDomains';
import { ControlledAutocompleteProps } from '@components/Inputs/controllers/ControlledAutocomplete';
import { ISelectOption } from '@constants/entities/ui';
import { useProject } from '@context/Project/ProjectProvider';
import useParsedDomains from '@hooks/useParsedDomains';
import { Stack } from '@mui/material';
import { transformArrayToOptions } from '@utils/helpers';

/**
 * Variant determines which domain fields are shown:
 * - targetOnly: Only Target Scope (editable) - for most entities
 * - targetWithOperationalised: Target Scope (editable) + Operationalised Scope (read-only) - for Service/Capability/Function
 * - operationalisedOnly: Only Operationalised Scope (editable) - for Technology
 */
type DomainDropdownVariant =
  | 'targetOnly'
  | 'targetWithOperationalised'
  | 'operationalisedOnly';

type Props = Partial<
  Pick<ControlledAutocompleteProps, 'name' | 'label' | 'required' | 'value'> & {
    disabled?: boolean;
    variant?: DomainDropdownVariant;
  }
>;

const DomainDropdown: FC<Props> = ({
  label,
  required = true,
  disabled,
  variant = 'targetWithOperationalised',
}) => {
  const { viewOnly } = useProject();

  const parsedDomains = useParsedDomains();

  const domains = useMemo<ISelectOption[]>(
    () =>
      parsedDomains
        .map(({ id, name, subDomains }) => [
          { value: id, label: name },
          ...subDomains.map(({ id, name, subDomains: sd }) => [
            { value: id, label: name },
            ...transformArrayToOptions(sd, {
              label: 'name',
              value: 'id',
            }),
          ]),
        ])
        .flat(3) as ISelectOption[],
    [parsedDomains],
  );

  const { inheritedDomains } = useVirtualDomains();

  // Determine the label based on variant
  const inputLabel = useMemo(() => {
    if (label) return label;
    return variant === 'operationalisedOnly'
      ? 'Operationalised Scope'
      : 'Target Scope';
  }, [label, variant]);

  // For operationalisedOnly variant, show only the editable dropdown
  if (variant === 'operationalisedOnly') {
    return (
      <DomainsInput
        label={inputLabel}
        required={required}
        options={domains}
        readOnly={viewOnly || !!disabled}
      />
    );
  }

  // For targetOnly variant, show only the Target Scope dropdown
  if (variant === 'targetOnly') {
    return (
      <DomainsInput
        label={inputLabel}
        required={required}
        options={domains}
        readOnly={viewOnly || !!disabled}
      />
    );
  }

  // For targetWithOperationalised variant, show both Target Scope and read-only Operationalised Scope
  return (
    <Stack gap={4}>
      <DomainsInput
        label={inputLabel}
        required={required}
        options={domains}
        readOnly={viewOnly || !!disabled}
      />

      <VirtualDomainsAccordion
        title="Operationalised Scope"
        domains={inheritedDomains}
      />
    </Stack>
  );
};

export default DomainDropdown;
